import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import './AddStation.scss'
import { useForm, Controller } from 'react-hook-form'

import breadcrumbBack from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/breadcrumb-back.svg'
import breadcrumbChevron from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/breadcrumb-chevron.svg'
import tickPms from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/tick-pms.svg'
import tickAgo from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/tick-ago.svg'
import tickLpg from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/tick-lpg.svg'
import tickEv from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/tick-ev.svg'
import tickEmpty from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/tick-empty.svg'
import uploadCloud from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/upload-cloud.svg'
import removeImage from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/remove-image.svg'
import tankIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/tank-icon.svg'
import closeCircleRed from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/close-circle-red.svg'

import fillingStationsService, {
  FillingStationProductType,
  UpsertFillingStationPayload,
} from '../../../../../../BoostXpressAdmin/Services/fillingStationsService'
import uploadDocumentService from 'Services/uploadDocumentService'
import { stationStaffService } from 'BoostXpressAdmin/Services/stationStaffService'
import {
  describeFailedAssignmentRoles,
  reconcileStationAssignments,
} from 'BoostXpressAdmin/Services/stationAssignmentReconciliation'
import { SearchableStaffSelect } from './components/SearchableStaffSelect'
import { filterStaffOptions, useStationStaffOptions } from './hooks/useStationStaffOptions'
import { getTankNamesError, tanksToSave } from './tankNames'

import statesRaw from 'Data/stateNG.json'
import lgasRaw from 'Data/lgaNG.json'
import { handleError, handleSuccess } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import customerService from 'Services/customerService'
import { userProfile } from 'Models'

type StateItem = { id: string; name: string; state_code?: string; extra?: string }
type LgaItem = { id: string; state_id: string; name: string; extra?: string }
type PlaceSuggestion = {
  place_id: string
  description: string
  location: { latitude: number; longitude: number }
}
type PlaceSearchResult = {
  id: string
  displayName?: { text?: string }
  formattedAddress?: string
  location: { latitude: number; longitude: number }
}
type SelectedLocation = {
  name: string
  address: string
  latitude: number
  longitude: number
}
const states = statesRaw as unknown as StateItem[]
const lgas = lgasRaw as unknown as LgaItem[]
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png']
const TRADEGRID_SUPPORTED_PRODUCTS = new Set(['PMS', 'AGO'])

export type TankType = {
  product: string
  quantity: number
  name?: string
}

type AddStationFormValues = {
  businessName: string
  branchName: string
  address: string
  state: string
  city: string
  fuelTypes: FillingStationProductType[]
  longitude: string
  latitude: string
  openingTimeHour: string
  closingTimeHour: string
  accountManagerAccountId: string
  stationManagerAccountId: string
  stationAssociateAccountId: string
  // fuelFactCompanyId: string
  // fuelFactCompanyBranchId: string
  isPublic: boolean
}

const AddStation = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const previousStateRef = useRef<string | null>(null)
  const [selectedImages, setSelectedImages] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])

  const [msisdn, setMsisdn] = useState<string>('')
  const [accountManagerInformation, setAccountManagerInformation] = useState<userProfile>()

  const { staffOptions, isLoadingStaff } = useStationStaffOptions()

  const [tanks, setTanks] = useState<TankType[]>([])
  const [newTankProduct, setNewTankProduct] = useState<string>('PMS')
  const [newTankCapacity, setNewTankCapacity] = useState<string>('')
  const [newTankName, setNewTankName] = useState<string>('')

  const [locationInputMode, setLocationInputMode] = useState<'manual' | 'search'>('manual')
  const [locationSearchInput, setLocationSearchInput] = useState('')
  const [locationSuggestions, setLocationSuggestions] = useState<PlaceSuggestion[]>([])
  const [isLocationSearching, setIsLocationSearching] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null)

  // Google Places API functions
  const searchPlaces = async (text: string) => {
    if (!text || text.length < 3) {
      setLocationSuggestions([])
      return
    }

    try {
      setIsLocationSearching(true)

      const res = await fetch('https://places.googleapis.com/v1/places:searchText', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': 'AIzaSyAmkXvvst5TGUQ07WZ0D0Xsg5n722tvKaQ',
          'X-Goog-FieldMask':
            'places.id,places.displayName,places.formattedAddress,places.location',
        },
        body: JSON.stringify({
          textQuery: text,
          maxResultCount: 6,
        }),
      })

      const data = (await res.json()) as { places?: PlaceSearchResult[] }

      setLocationSuggestions(
        (data.places || []).map((place) => ({
          // eslint-disable-next-line camelcase
          place_id: place.id,
          description: `${place.displayName?.text || ''} — ${place.formattedAddress || ''}`,
          location: place.location,
        })),
      )
    } catch (err) {
      console.error(err)
      setLocationSuggestions([])
    } finally {
      setIsLocationSearching(false)
    }
  }

  // const getPlaceDetails = async (placeId: string) => {
  //   try {
  //     const response = await fetch(
  //       `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=geometry,name,formatted_address&key=AIzaSyAmkXvvst5TGUQ07WZ0D0Xsg5n722tvKaQ`,
  //     )
  //     const data = await response.json()

  //     if (data.status === 'OK' && data.result) {
  //       const place = data.result
  //       const location = place.geometry.location

  //       // Update form values with latitude and longitude
  //       setValue('latitude', location.lat.toString())
  //       setValue('longitude', location.lng.toString())

  //       // Update selected location state
  //       setSelectedLocation({
  //         name: place.name,
  //         address: place.formatted_address,
  //         latitude: location.lat,
  //         longitude: location.lng,
  //       })

  //       // Clear search input and suggestions
  //       setLocationSearchInput(place.formatted_address || place.name)
  //       setLocationSuggestions([])
  //     }
  //   } catch (error) {
  //     console.error('Error getting place details:', error)
  //   }
  // }

  // const handleLocationSelect = (suggestion: any) => {
  //   getPlaceDetails(suggestion.place_id)
  // }

  const handleLocationSelect = (item: PlaceSuggestion) => {
    setSelectedLocation({
      name: item.description,
      address: item.description,
      latitude: item.location.latitude,
      longitude: item.location.longitude,
    })

    setValue('latitude', item.location.latitude.toString())
    setValue('longitude', item.location.longitude.toString())

    // Clear search input and suggestions
    setLocationSearchInput(item.description)
    setLocationSuggestions([])
  }

  const handleLocationInputChange = (value: string) => {
    setLocationSearchInput(value)
    searchPlaces(value)
  }

  const switchToManualMode = () => {
    setLocationInputMode('manual')
    setLocationSearchInput('')
    setLocationSuggestions([])
    setSelectedLocation(null)
  }

  const switchToSearchMode = () => {
    setLocationInputMode('search')
    setSelectedLocation(null)
  }

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { isSubmitting },
  } = useForm<AddStationFormValues>({
    defaultValues: {
      businessName: '',
      branchName: '',
      address: '',
      state: '',
      city: '',
      fuelTypes: [],
      longitude: '',
      latitude: '',
      openingTimeHour: '',
      closingTimeHour: '',
      accountManagerAccountId: '',
      stationManagerAccountId: '',
      stationAssociateAccountId: '',
      // fuelFactCompanyId: '',
      // fuelFactCompanyBranchId: '',
      isPublic: false,
    },
  })

  const selectedStateName = watch('state')
  const selectedStationManagerId = watch('stationManagerAccountId')
  const selectedStationAssociateId = watch('stationAssociateAccountId')
  const managerOptions = useMemo(
    () =>
      filterStaffOptions(
        staffOptions,
        'STATION_MANAGER',
        selectedStationAssociateId,
        selectedStationManagerId,
      ),
    [selectedStationAssociateId, selectedStationManagerId, staffOptions],
  )
  const associateOptions = useMemo(
    () =>
      filterStaffOptions(
        staffOptions,
        'STATION_ASSOCIATE',
        selectedStationManagerId,
        selectedStationAssociateId,
      ),
    [selectedStationAssociateId, selectedStationManagerId, staffOptions],
  )
  const selectedStateId = useMemo(
    () => states.find((s) => s.name === selectedStateName)?.id,
    [selectedStateName],
  )
  const lgaOptions = useMemo(() => {
    if (!selectedStateId) return []
    return lgas.filter((l) => l.state_id === selectedStateId).map((l) => l.name)
  }, [selectedStateId])

  const selectedFuelTypes = (watch('fuelTypes') ?? []) as FillingStationProductType[]
  const isFuelSelected = (product: string) => selectedFuelTypes.some((p) => p.product === product)

  const toggleFuel = (product: string) => {
    const exists = isFuelSelected(product)
    const next = exists
      ? selectedFuelTypes.filter((p) => p.product !== product)
      : [
          ...selectedFuelTypes,
          {
            product,
            price: 0,
            priceCurrency: 'NGN',
            unitOfMeasure: product === 'LPG' ? 'Kilogram' : 'Litre',
            isAvailable: true,
          },
        ]

    setValue('fuelTypes', next, { shouldDirty: true, shouldTouch: true })
  }

  const updateFuelDetail = (
    product: string,
    field: keyof FillingStationProductType,
    value: FillingStationProductType[keyof FillingStationProductType],
  ) => {
    const next = selectedFuelTypes.map((p) => {
      if (p.product === product) {
        return { ...p, [field]: value }
      }
      return p
    })
    setValue('fuelTypes', next, { shouldDirty: true, shouldTouch: true })
  }

  const addTank = () => {
    if (!newTankCapacity || isNaN(Number(newTankCapacity))) return
    setTanks([
      ...tanks,
      { product: newTankProduct, quantity: Number(newTankCapacity), name: newTankName },
    ])
    setNewTankCapacity('')
    setNewTankName('')
  }

  const removeTank = (index: number) => {
    setTanks(tanks.filter((_, i) => i !== index))
  }

  const renameTank = (index: number, name: string) => {
    setTanks(tanks.map((tank, i) => (i === index ? { ...tank, name } : tank)))
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return

    const invalidFiles = files.filter((file) => !ALLOWED_IMAGE_TYPES.includes(file.type))
    if (invalidFiles.length) {
      handleError({
        response: {
          data: {
            error: 'Only JPG, JPEG, and PNG images are allowed.',
          },
        },
      })
      e.target.value = ''
      return
    }

    const newFiles = [...selectedImages, ...files]
    setSelectedImages(newFiles)

    const newPreviews = files.map((file) => URL.createObjectURL(file))
    setImagePreviews([...imagePreviews, ...newPreviews])
    e.target.value = ''
  }

  const removeImagePreview = (index: number) => {
    const previewToRemove = imagePreviews[index]

    setSelectedImages(selectedImages.filter((_, i) => i !== index))
    setImagePreviews(imagePreviews.filter((_, i) => i !== index))

    URL.revokeObjectURL(previewToRemove)
  }

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])

  useEffect(() => {
    if (previousStateRef.current !== null && previousStateRef.current !== selectedStateName) {
      setValue('city', '')
    }
    previousStateRef.current = selectedStateName
  }, [selectedStateName, setValue])

  const getUserByMsisdn = async (phone: string) => {
    const trimmed = phone.trim()
    if (!trimmed || trimmed.length < 10) {
      setAccountManagerInformation(undefined)
      setValue('accountManagerAccountId', '')
      return
    }

    const searchEndpoint = `customerMsisdn=${encodeURIComponent(trimmed)}`

    try {
      const res = await customerService.searchCustomer(searchEndpoint, 1, 'DESC', undefined)
      const user = res.data.data.dataset?.[0]
      if (user) {
        setAccountManagerInformation(user)
        setValue('accountManagerAccountId', user.accountId ?? '')
      } else {
        setAccountManagerInformation(undefined)
        setValue('accountManagerAccountId', '')
      }
    } catch (e) {
      setAccountManagerInformation(undefined)
      setValue('accountManagerAccountId', '')
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (msisdn) getUserByMsisdn(msisdn)
    }, 500)
    return () => clearTimeout(timer)
  }, [msisdn])

  const onSubmit = async (values: AddStationFormValues) => {
    const tankNamesError = getTankNamesError(tanks)
    if (tankNamesError) {
      handleError({ response: { data: { error: tankNamesError } } })
      return
    }

    if (
      values.stationManagerAccountId &&
      values.stationManagerAccountId === values.stationAssociateAccountId
    ) {
      handleError({
        response: {
          data: { error: 'The station manager and station associate must be different users.' },
        },
      })
      return
    }

    const latitude = Number(values.latitude)
    const longitude = Number(values.longitude)

    const payload: UpsertFillingStationPayload = {
      businessName: values.businessName.trim(),
      branchName: values.branchName.trim(),
      address: values.address.trim(),
      state: values.state.trim(),
      city: values.city.trim(),
      latitude: Number.isFinite(latitude) ? latitude : 0,
      longitude: Number.isFinite(longitude) ? longitude : 0,
      accountManagerAccountId: values.accountManagerAccountId.trim(),
      // fuelFactCompanyId: values.fuelFactCompanyId.trim(),
      // fuelFactCompanyBranchId: values.fuelFactCompanyBranchId.trim(),
      openingTimeHour: values.openingTimeHour.trim(),
      closingTimeHour: values.closingTimeHour.trim(),
      products: values.fuelTypes ?? [],
      tanks: tanksToSave(tanks),
      isPublic: values.isPublic,
    }

    try {
      const res = await fillingStationsService.create(payload)
      const stationId = res.data?.id

      if (!res.status || !stationId) {
        handleError({
          response: {
            data: {
              error:
                res.error || res.errors?.[0]?.message || res.message || 'Failed to create station.',
            },
          },
        })
        return
      }

      const assignmentResults = await reconcileStationAssignments(
        stationId,
        [],
        {
          STATION_MANAGER: values.stationManagerAccountId,
          STATION_ASSOCIATE: values.stationAssociateAccountId,
        },
        {
          createAssignment: stationStaffService.createAssignment,
          deleteAssignment: stationStaffService.deleteAssignment,
        },
      )
      const failedRoles = describeFailedAssignmentRoles(assignmentResults)

      if (failedRoles) {
        handleError({
          response: {
            data: {
              error: `Station created, but the ${failedRoles} assignment failed. Continue from Edit Station.`,
            },
          },
        })
        navigate(`/boostxpress/stations/${stationId}/edit`)
        return
      }

      if (selectedImages.length) {
        await startUpload(stationId, selectedImages)
        return
      }

      handleSuccess('Station created successfully.')
      navigate('/boostxpress/stations')
    } catch (error) {
      handleError(error as Parameters<typeof handleError>[0])
    }
  }

  const startUpload = async (stationId: string, selectedImages: File[]) => {
    const failedUploads: string[] = []

    for (const [, file] of selectedImages.entries()) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        await uploadDocumentService.upload('FILLING_STATION_IMAGES', stationId, formData)
      } catch {
        failedUploads.push(file.name)
      }
    }

    if (failedUploads.length === 0) {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url))
      setSelectedImages([])
      setImagePreviews([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      handleSuccess('Station created successfully.')
      navigate('/boostxpress/stations')
      return
    }

    handleError({
      response: {
        data: {
          error: `Station created, but ${failedUploads.length} image upload(s) failed: ${failedUploads.join(', ')}`,
        },
      },
    })
  }

  const managerName = accountManagerInformation
    ? `${accountManagerInformation.firstName ?? ''} ${accountManagerInformation.lastName ?? ''}`.trim()
    : ''

  return (
    <div className='bx-station-form-page'>
      <div className='bx-station-form-page__breadcrumb'>
        <button className='bx-station-form-page__back' onClick={() => navigate(-1)}>
          <img src={breadcrumbBack} alt='Back' className='bx-station-form-page__back-icon' />
        </button>
        <div className='bx-station-form-page__crumbs'>
          <Link to='/boostxpress/stations' className='bx-station-form-page__crumb-link'>
            Stations Management
          </Link>
          <img src={breadcrumbChevron} alt='' className='bx-station-form-page__crumb-sep' />
          <span className='bx-station-form-page__crumb-current'>Add New Station</span>
        </div>
      </div>

      <h2 className='bx-station-form-page__title'>Add New Station</h2>

      <form className='bx-station-form-card' onSubmit={handleSubmit(onSubmit)}>
        <div className='bx-station-form-grid'>
          <div className='bx-station-field bx-station-field--full'>
            <label className='bx-station-field__label'>Station MSISDN</label>
            <input
              className='bx-station-field__input'
              placeholder='e.g +2348012345678'
              value={msisdn}
              onChange={(e) => setMsisdn(e.target.value)}
            />
          </div>

          <div className='bx-station-field bx-station-field--full'>
            <label className='bx-station-field__label'>Account Manager *</label>
            <input
              className='bx-station-field__input'
              placeholder='Found manager name'
              value={managerName}
              readOnly
              disabled
            />
          </div>

          <div className='bx-station-field bx-station-field--full'>
            <label className='bx-station-field__label'>Station Manager</label>
            <Controller
              name='stationManagerAccountId'
              control={control}
              render={({ field }) => (
                <SearchableStaffSelect
                  options={managerOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder='Select Station Manager'
                  clearLabel='No station manager'
                  isLoading={isLoadingStaff}
                  disabled={isSubmitting}
                />
              )}
            />
          </div>

          <div className='bx-station-field bx-station-field--full'>
            <label className='bx-station-field__label'>Station Associate</label>
            <Controller
              name='stationAssociateAccountId'
              control={control}
              render={({ field }) => (
                <SearchableStaffSelect
                  options={associateOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder='Select Station Associate'
                  clearLabel='No station associate'
                  isLoading={isLoadingStaff}
                  disabled={isSubmitting}
                />
              )}
            />
          </div>

          <div className='bx-station-field'>
            <label className='bx-station-field__label'>Business Name *</label>
            <input
              className='bx-station-field__input'
              placeholder='e.g Total Energies Lekki'
              {...register('businessName', { required: true })}
            />
          </div>

          <div className='bx-station-field'>
            <label className='bx-station-field__label'>Branch Name</label>
            <input
              className='bx-station-field__input'
              placeholder='e.g Lekki Phase 1'
              {...register('branchName')}
            />
          </div>

          <div className='bx-station-address-row'>
            <div className='bx-station-field'>
              <label className='bx-station-field__label'>Full Address *</label>
              <input
                className='bx-station-field__input'
                placeholder='Street'
                {...register('address', { required: true })}
              />
            </div>

            <div className='bx-station-field'>
              <label className='bx-station-field__label'>State *</label>
              <select
                className='bx-station-field__select form-control'
                {...register('state', { required: true })}
              >
                <option value='' disabled>
                  Select State
                </option>
                {states.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='bx-station-field'>
              <label className='bx-station-field__label'>City *</label>
              <select
                className='bx-station-field__select form-control'
                {...register('city', { required: true })}
                disabled={!selectedStateId}
              >
                <option value='' disabled>
                  Select Local Government
                </option>
                {lgaOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* <div className='bx-station-field'>
            <label className='bx-station-field__label'>Longitude</label>
            <input
              className='bx-station-field__input'
              placeholder='6.4281'
              {...register('longitude')}
            />
          </div>

          <div className='bx-station-field'>
            <label className='bx-station-field__label'>Latitude</label>
            <input
              className='bx-station-field__input'
              placeholder='3.4219'
              {...register('latitude')}
            />
          </div> */}

          <div className='bx-station-field bx-station-field--full'>
            <div className='row w-100 align-items-start'>
              <div className='col-auto'>
                <div className='bx-station-field mt-4'>
                  {/* <label className='bx-station-field__label d-block'>Location</label> */}
                  <div className='bx-location-input-toggle'>
                    <button
                      type='button'
                      className={`bx-location-toggle-btn ${locationInputMode === 'manual' ? 'active' : ''}`}
                      onClick={switchToManualMode}
                      title='Manual Entry'
                    >
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
                          fill='currentColor'
                        />
                      </svg>
                    </button>
                    <button
                      type='button'
                      className={`bx-location-toggle-btn ${locationInputMode === 'search' ? 'active' : ''}`}
                      onClick={switchToSearchMode}
                      title='Search Location'
                    >
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'
                          fill='currentColor'
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              {locationInputMode === 'manual' ? (
                <>
                  <div className='col'>
                    <label className='bx-station-field__label'>Longitude</label>
                    <input
                      type='number'
                      step={'any'}
                      className='form-control'
                      placeholder='6.4281'
                      {...register('longitude')}
                    />
                  </div>

                  <div className='col'>
                    <label className='bx-station-field__label'>Latitude</label>
                    <input
                      type='number'
                      step={'any'}
                      className='form-control'
                      placeholder='3.4219'
                      {...register('latitude')}
                    />
                  </div>
                </>
              ) : (
                <div className='col'>
                  <label className='bx-station-field__label'>Search Location</label>
                  <div className='position-relative'>
                    <div className='bx-location-search'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Search for a location...'
                        value={locationSearchInput}
                        onChange={(e) => handleLocationInputChange(e.target.value)}
                      />
                      {isLocationSearching && (
                        <div className='bx-location-search-loading'>
                          <div className='spinner-border spinner-border-sm' role='status'>
                            <span className='visually-hidden'>Searching...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {locationSuggestions.length > 0 && (
                      <div className='bx-location-suggestions'>
                        {locationSuggestions.map((suggestion, index) => (
                          <div
                            key={suggestion['place_id'] || index}
                            className='bx-location-suggestion-item'
                            onClick={() => handleLocationSelect(suggestion)}
                          >
                            <div className='bx-location-suggestion-text'>
                              <div className='bx-location-suggestion-name'>
                                {suggestion.description?.split(' - ')[0] || suggestion.description}
                              </div>
                              <div className='bx-location-suggestion-address'>
                                {suggestion.description?.split(' - ')[1] || suggestion.description}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedLocation && (
                    <div className='bx-selected-location-info'>
                      <div className='bx-selected-location-text'>
                        <strong>Selected Location:</strong>{' '}
                        {selectedLocation.name || selectedLocation.address}
                      </div>
                      <div className='bx-selected-location-coords'>
                        <span>Lat: {selectedLocation.latitude.toFixed(6)}</span>
                        <span>Lng: {selectedLocation.longitude.toFixed(6)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className='bx-station-field'>
            <label className='bx-station-field__label'>Opening Time</label>
            <input
              className='bx-station-field__input'
              placeholder='8am'
              type='time'
              {...register('openingTimeHour')}
            />
          </div>

          <div className='bx-station-field'>
            <label className='bx-station-field__label'>Closing Time</label>
            <input
              className='bx-station-field__input'
              placeholder='9pm'
              type='time'
              {...register('closingTimeHour')}
            />
          </div>

          <input
            className='bx-station-field__input d-none'
            placeholder='Account Manager Account ID'
            readOnly
            {...register('accountManagerAccountId')}
          />

          {/* <div className='bx-station-field'>
            <label className='bx-station-field__label'>FuelFact Company ID</label>
            <input
              className='bx-station-field__input'
              placeholder='FuelFact Company ID'
              {...register('fuelFactCompanyId')}
            />
          </div>

          <div className='bx-station-field bx-station-field'>
            <label className='bx-station-field__label'>FuelFact Company Branch ID</label>
            <input
              className='bx-station-field__input'
              placeholder='FuelFact Company Branch ID'
              {...register('fuelFactCompanyBranchId')}
            />
          </div> */}

          <div className='bx-station-field bx-station-field--full'>
            <label className='bx-station-field__label'>Station Visibility</label>
            <div className='bx-station-radio-group'>
              <label className='bx-station-radio'>
                <input type='radio' value='true' {...register('isPublic')} defaultChecked />
                <span className='bx-station-radio__label'>Public</span>
              </label>
              <label className='bx-station-radio ms-3'>
                <input type='radio' value='false' {...register('isPublic')} />
                <span className='bx-station-radio__label'>Private</span>
              </label>
            </div>
          </div>
        </div>

        <div className='bx-station-fuel-prices-section'>
          <p className='bx-station-fuel-prices-section__header'>Fuel Types & Prices</p>

          <div className='bx-station-fuel-section'>
            <p className='bx-station-fuel-section__title'>Fuel Types</p>
            <div className='bx-station-fuel-types'>
              {[
                { id: 'PMS', icon: tickPms, label: 'PMS' },
                { id: 'AGO', icon: tickAgo, label: 'AGO' },
                { id: 'LPG', icon: tickLpg, label: 'LPG' },
                { id: 'EV', icon: tickEv, label: 'EV' },
              ].map((fuel) => {
                const isSupported = TRADEGRID_SUPPORTED_PRODUCTS.has(fuel.id)
                return (
                  <label
                    key={fuel.id}
                    className={`bx-station-fuel${isSupported ? '' : ' bx-station-fuel--disabled'}`}
                    title={isSupported ? undefined : 'Not currently supported by the station API'}
                  >
                    <input
                      type='checkbox'
                      className='bx-station-fuel__checkbox'
                      checked={isFuelSelected(fuel.id)}
                      onChange={() => toggleFuel(fuel.id)}
                      disabled={!isSupported}
                    />
                    <img
                      src={isFuelSelected(fuel.id) ? fuel.icon : tickEmpty}
                      alt=''
                      className='bx-station-fuel__icon'
                    />
                    <span className='bx-station-fuel__label'>{fuel.label}</span>
                  </label>
                )
              })}
            </div>
            <p className='bx-station-fuel-section__notice'>
              LPG and EV are unavailable until the station API supports them.
            </p>
          </div>

          <div className='bx-station-fuel-details-list'>
            {selectedFuelTypes.map((fuel) => (
              <div key={fuel.product} className='bx-station-fuel-row'>
                <div className='bx-station-field'>
                  <label className='bx-station-field__label'>{fuel.product} Price (₦)</label>
                  <input
                    className='bx-station-field__input'
                    placeholder='e.g ₦650'
                    type='number'
                    value={fuel.price || ''}
                    onChange={(e) =>
                      updateFuelDetail(fuel.product, 'price', Number(e.target.value))
                    }
                  />
                </div>
                <div className='bx-station-field'>
                  <label className='bx-station-field__label'>Unit of Measurement</label>
                  <select
                    className='bx-station-field__select form-control'
                    value={fuel.unitOfMeasure}
                    onChange={(e) =>
                      updateFuelDetail(fuel.product, 'unitOfMeasure', e.target.value)
                    }
                  >
                    <option value='Litre'>Litres</option>
                    <option value='Kilogram'>Kilogram</option>
                  </select>
                </div>
                <div className='bx-station-field'>
                  <label className='bx-station-field__label'>Status</label>
                  <select
                    className='bx-station-field__select form-control'
                    value={fuel.isAvailable ? 'Available' : 'Unavailable'}
                    onChange={(e) =>
                      updateFuelDetail(fuel.product, 'isAvailable', e.target.value === 'Available')
                    }
                  >
                    <option value='Available'>Available</option>
                    <option value='Unavailable'>Unavailable</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='bx-station-tanks-section'>
          <p className='bx-station-tanks-section__header'>Add Tanks</p>

          <div className='bx-station-tanks-list'>
            {tanks.map((tank, idx) => (
              <div key={idx} className='bx-tank-card'>
                <img src={tankIcon} alt='' className='bx-tank-card__icon' />
                <div className='bx-tank-card__info'>
                  <input
                    className='bx-station-field__input'
                    placeholder='Name (all tanks or none)'
                    aria-label={`Tank ${idx + 1} name`}
                    value={tank.name ?? ''}
                    onChange={(e) => renameTank(idx, e.target.value)}
                  />
                  <span className='bx-tank-card__product'>{tank.product}</span>
                  <span className='bx-tank-card__capacity'>
                    {tank.quantity} ({tank.product === 'LPG' ? 'Kg' : 'L'})
                  </span>
                </div>
                <button
                  type='button'
                  className='bx-tank-card__remove'
                  onClick={() => removeTank(idx)}
                >
                  <img src={closeCircleRed} alt='Remove' />
                </button>
              </div>
            ))}
          </div>

          <div className='bx-station-tank-add-row'>
            <div className='bx-station-field'>
              <label className='bx-station-field__label'>Product Type</label>
              <select
                className='bx-station-field__select form-control'
                value={newTankProduct}
                onChange={(e) => setNewTankProduct(e.target.value)}
              >
                <option value='PMS'>PMS</option>
                <option value='AGO'>AGO</option>
              </select>
            </div>
            <div className='bx-station-field'>
              <label className='bx-station-field__label'>Capacity</label>
              <input
                className='bx-station-field__input'
                placeholder='e.g 5000'
                type='number'
                value={newTankCapacity}
                onChange={(e) => setNewTankCapacity(e.target.value)}
              />
            </div>
            <div className='bx-station-field'>
              <label className='bx-station-field__label' htmlFor='new-tank-name'>
                Name (all tanks or none)
              </label>
              <input
                className='bx-station-field__input'
                id='new-tank-name'
                placeholder='e.g Front'
                value={newTankName}
                onChange={(e) => setNewTankName(e.target.value)}
              />
            </div>
            <button type='button' className='bx-station-tank-add-btn' onClick={addTank}>
              + Add
            </button>
          </div>
        </div>

        <div className='bx-station-upload-section'>
          <p className='bx-station-upload-section__title'>Upload Station Images</p>
          <div className='bx-station-upload-container'>
            <div className='bx-station-upload-drop' onClick={() => fileInputRef.current?.click()}>
              <img src={uploadCloud} alt='' className='bx-station-upload-drop__icon' />
              <span className='bx-station-upload-drop__text'>
                Drag files here or click to upload
              </span>
              <input
                ref={fileInputRef}
                type='file'
                className='bx-station-upload-drop__input'
                accept='.jpg,.jpeg,.png,image/jpeg,image/png'
                multiple
                onChange={handleImageChange}
              />
            </div>

            <div className='bx-station-upload-previews'>
              {imagePreviews.map((url, idx) => (
                <div key={idx} className='bx-image-preview-item'>
                  <div
                    className='bx-image-preview-thumb'
                    style={{ backgroundImage: `url(${url})` }}
                  />
                  <button
                    type='button'
                    className='bx-image-preview-remove'
                    onClick={() => removeImagePreview(idx)}
                  >
                    <img src={removeImage} alt='Remove' />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className='bx-station-form-actions'>
          <button className='bx-station-submit-btn' type='submit' disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Add Station'}
          </button>
        </div>
      </form>

      <ToastContainer />
    </div>
  )
}

export default AddStation
